/**
 * CoverageRequirementPanel — master–detail side panel for one checklist row.
 *
 * Consolidates sources, report fields, and actions for the selected
 * coverage requirement.
 */
import React from 'react';
import { Upload, ExternalLink, Pencil, Plus, HelpCircle, ChevronRight } from 'lucide-react';
import type { ChecklistItem } from '../../../config/documentChecklists';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import { PsePrefetchCta } from '../PsePrefetchCta';
import { evidenceProbeFor } from '../../../lib/coverage/evidenceProbes';
import type { ExtractedClaim } from '../../../types/siteContext';
import type { RequirementLineage } from '../../../lib/evidenceLineage/requirementLineage';
import type { RequirementCoverage } from '../../../lib/coverage/requirementCoverage';
import { getDocumentChecklistFor } from '../../../config/documentChecklists';
import {
  STRONG_STRUCTURED_FRACTION,
} from '../../../lib/coverage/evaluateRequirementCompleteness';
import { resolveAutomaticReportFields } from '../../../lib/coverage/resolveAutomaticReportFields';
import { compileCoverageAutoFillFromSed, type RequirementAutoFill } from '../../../lib/coverage/compileCoverageAutoFill';
import { resolveReportSetupFields } from '../../../lib/coverage/resolveReportSetupFields';
import { isNonDocumentRequirement, setupReportTypeForRequirement } from '../../../config/reportSetupRequirements';
import { RequirementInlineSetupForm } from './RequirementInlineSetupForm';
import { summarizePopulateSourcesForRequirement } from '../../../lib/coverage/populateSourceAudit';
import { CoverageCellFillSource } from './CoverageCellFillSource';
import {
  CELL_CLASS,
  CELL_GLYPH,
  CELL_LABEL,
  FieldProvenanceChip,
  SOURCE_SUMMARY_ROWS,
  type CoverageCellStatus,
} from './coverageMatrixUi';
import type { SiteDocument } from '../../../types/siteRag';
import type { RankedDocMatch } from '../../../config/documentChecklists';
import type { DocSufficiency } from '../docChecklist/sufficiency';
import type { SiteContext } from '../../../types/siteContext';
import { RequirementVariableAccordion } from './RequirementVariableAccordion';
import { RequirementInlineAuthorForm } from './RequirementInlineAuthorForm';
import { ScreeningFacts } from './ScreeningFacts';
import { ExtractedFieldReviewPanel } from './ExtractedFieldReviewPanel';
import {
  extractedFieldsForRequirement,
  type FieldReviewDecision,
} from '../../../lib/coverage/extractedFieldReview';
import type { StructuredEvidenceValue } from '../../../types/siteEvidence';
import { useStore } from '../../../store';
import { toast } from 'sonner';
import { isUnifiedEvidencePanelEnabled } from '../../../lib/runtimeFlags';
import { resolveRequirementFieldPresentation } from '../../../lib/coverage/resolveRequirementFieldPresentation';
import type { ReportTypeId } from '../../../config/reportTypes';
import type { ProjectIntent, TypedPreferencesByType } from '../../../types/siteRag';
import { evidenceRequirementForStage, evidenceFieldsWithRecordedUnknowns, evidenceResolvedFieldsForStage, type ReviewedEvidenceFields } from '../../../lib/coverage/stageEvidenceFields';

/**
 * Review host for machine-extracted values — the post-closure surface.
 *
 * The Accept/Edit/Reject panel originally lived only on OPEN gap cards in
 * the Evidence ledger, so the moment extraction filled enough fields to
 * close a gap, the review actions vanished with the card — exactly when a
 * wrong extracted value does the most damage (it is now scoring). This
 * host renders the same panel in the Coverage Matrix requirement detail
 * whenever the requirement's primary section carries extracted fields,
 * open gap or not. Same store action, same review-as-metadata contract:
 * a decision never promotes the evidence tier.
 */
function RequirementFieldReviewHost({
  item,
  sed,
  siteId,
  documents,
  onReloadSed,
}: {
  item: ChecklistItem;
  sed?: SiteEvidenceDocument;
  siteId?: string;
  documents?: readonly SiteDocument[];
  onReloadSed?: () => void;
}) {
  const [busyKey, setBusyKey] = React.useState<string | null>(null);
  const rows = React.useMemo(
    () => extractedFieldsForRequirement(sed, item.id, item),
    [sed, item],
  );
  const documentNames = React.useMemo(() => {
    if (!sed || rows.length === 0) return [];
    const names = new Set<string>();
    for (const sectionId of new Set(rows.map(r => r.sectionId))) {
      const slot = (sed as unknown as Record<string, {
        supportingDocIds?: string[];
        provenanceDetail?: { docId?: string };
      } | undefined>)[sectionId];
      for (const docId of [
        ...(slot?.supportingDocIds ?? []),
        ...(slot?.provenanceDetail?.docId ? [slot.provenanceDetail.docId] : []),
      ]) {
        const name = documents?.find(d => d.id === docId)?.fileName;
        if (name) names.add(name);
      }
    }
    return [...names];
  }, [sed, rows, documents]);

  const decide = React.useCallback(async (
    sectionId: string,
    fieldKey: string,
    decision: FieldReviewDecision | null,
    correctedValue?: StructuredEvidenceValue,
  ) => {
    if (!siteId || busyKey) return;
    setBusyKey(fieldKey);
    try {
      const res = await useStore.getState().reviewExtractedField(
        siteId, sectionId, fieldKey, decision, correctedValue,
      );
      if (!res.ok) {
        toast.error(res.reason ?? 'The review could not be saved.');
        return;
      }
      toast.success(
        decision === null ? 'Review undone.'
          : decision === 'rejected' ? 'Marked wrong — the value was removed.'
            : decision === 'corrected' ? 'Value corrected.'
              : 'Confirmed against the document.',
      );
      onReloadSed?.();
    } catch (e) {
      toast.error((e as Error)?.message ?? 'The review could not be saved.');
    } finally {
      setBusyKey(null);
    }
  }, [siteId, busyKey, onReloadSed]);

  if (!siteId || rows.length === 0) return null;
  return (
    <div data-testid="coverage-field-review-host">
      <ExtractedFieldReviewPanel
        rows={rows}
        documentNames={documentNames}
        busyFieldKey={busyKey}
        onDecide={(row, decision, corrected) => { void decide(row.sectionId, row.fieldKey, decision, corrected); }}
        onUndo={(row) => { void decide(row.sectionId, row.fieldKey, null); }}
      />
    </div>
  );
}

export interface CoverageRequirementPanelProps {
  item: ChecklistItem | null;
  authoringItem?: ChecklistItem;
  status: CoverageCellStatus;
  authoredClaims?: readonly ExtractedClaim[];
  lineage: RequirementLineage | null;
  sed?: SiteEvidenceDocument;
  coverage?: RequirementCoverage;
  matchCount?: number;
  topMatch?: { doc: SiteDocument; row: RankedDocMatch; sufficiency: DocSufficiency } | null;
  variant?: 'panel' | 'drawer' | 'responsive';
  onOpenAuthor: () => void;
  onOpenUploadFor: () => void;
  onOpenInDocuments: () => void;
  onOpenBaseline?: () => void;
  onAddSupportingDoc?: () => void;
  onReplaceDoc?: () => void;
  onOpenLineage?: (lineage: RequirementLineage) => void;
  onViewVariables?: (fieldPath?: string) => void;
  /** In-panel variable deep-link (unified evidence panel). */
  focusVariablePath?: string | null;
  forceExpandVariables?: boolean;
  siteContext?: SiteContext | null;
  documents?: readonly SiteDocument[];
  siteId?: string;
  onOpenDocument?: (docId: string) => void;
  onReloadSed?: () => void;
  /** Optional already compiled source projection from the Matrix host. */
  autoFillEntry?: RequirementAutoFill;
  /** @deprecated Fields now derive automatically; this writer is never called. */
  onPopulateFromData?: () => void;
  isPopulating?: boolean;
  populateError?: string | null;
  /** Override unified panel flag (for tests). */
  unifiedEvidencePanel?: boolean;
  /** Called after inline manual entry saves (same hook as Author dialog). */
  onAuthorSaved?: (result: { claimId: string; requirementId: string }) => void;
  /** Report types in scope — drives field prioritisation and guidance copy. */
  reportTypes?: readonly ReportTypeId[];
  /** Site project intent — drives load/cooling-aware field ranking. */
  projectIntent?: ProjectIntent;
  /** Site typed preference defaults — setup row resolution + inline edit. */
  typedPreferences?: TypedPreferencesByType;
  /** Opens Site Project Intent editor for site-intent rows. */
  onOpenSiteIntent?: (fieldKey?: string) => void;
  /**
   * The site's decision stage. During early screening (1) the Site Project
   * Intent row lists only screening fields as gaps; Stage 2 decision fields
   * are noted as later work.
   */
  decisionStage?: 1 | 2;
  /** Waive this requirement explicitly (e.g. accepted gap). */
  onWaiveRequirement?: (requirementId: string, reason: string, category: 'not-applicable' | 'data-unavailable' | 'accepted-gap' | 'will-provide') => Promise<void>;
  onPreviousTopic?: () => void;
  onNextTopic?: () => void;
  sourceDetails?: React.ReactNode;
}

function sourceCount(
  lineage: RequirementLineage,
  kind: typeof SOURCE_SUMMARY_ROWS[number]['kind'],
): number {
  const c = lineage.counts;
  switch (kind) {
    case 'dam': return c.damSources;
    case 'provider': return c.providerSources;
    case 'doc': return c.docSources;
    case 'authored': return c.authoredClaims;
    case 'research': return c.siteResearchSources;
    case 'prior': return c.priorReportSources;
  }
}

function CompletenessBar({ filled, total, reviewedContext = false, reviewed = filled }: { filled: number; total: number; reviewedContext?: boolean; reviewed?: number }) {
  if (total <= 0) return null;
  const pct = Math.min(100, Math.round((filled / total) * 100));
  const strongThreshold = Math.ceil(total * STRONG_STRUCTURED_FRACTION);
  const filledForBar = Math.min(filled, total);
  return (
    <div className="space-y-1" data-testid="coverage-requirement-completeness">
      <div className="flex items-center justify-between gap-2 text-[10px] text-text-muted">
        <span className="shrink-0">{reviewedContext ? reviewed : filled}/{total} {reviewedContext ? 'context fields reviewed' : 'fields'}</span>
        <span className="shrink-0 tabular-nums">{pct}%{reviewedContext ? ' known' : ''}</span>
      </div>
      <div className="h-1.5 rounded-full bg-state-hover overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${Math.min(100, Math.round((filledForBar / total) * 100))}%` }}
        />
      </div>
      {reviewedContext ? (
        <p className="type-body text-text-muted">{filled}/{total} fields contain values. Recorded unknowns remain unresolved; review does not establish engineering capacity or clear report holds.</p>
      ) : filledForBar < strongThreshold && (
        <p className="type-body text-text-muted">
          Fill {strongThreshold - filledForBar} more field{strongThreshold - filledForBar === 1 ? '' : 's'} to reach Strong.
        </p>
      )}
    </div>
  );
}

const SOURCE_TOOLTIP =
  'Field sources: 🤖 DAM · 🔌 API · 📄 Doc · ✍ Authored · 🔍 Research · 📊 Prior report';

const FIELD_ROW_CLASS =
  'grid grid-cols-1 gap-x-2 gap-y-1 md:grid-cols-[minmax(0,8rem)_minmax(0,1fr)_auto] md:items-start text-[10px]';

const TIER_LABELS: Record<string, string> = {
  dam: 'DAM',
  sed: 'API / SED',
  doc: 'User doc',
  research: 'AI research',
  car: 'Auto-fill',
};

/** Requirement-specific automatic source guidance (PSR-2). */
const AUTOMATIC_SOURCE_HINTS: Partial<Record<string, string>> = {
  'community-feedback-summary':
    'Prefilled from public records — upload stakeholder log to override.',
  'zoning':
    'Regrid may fill code and permitted uses only — upload county verification for setbacks and height.',
};

function AutomaticSourceHint({ lineage }: { lineage: RequirementLineage }) {
  const summary = React.useMemo(
    () => summarizePopulateSourcesForRequirement(lineage.requirementId, lineage.counts),
    [lineage],
  );
  const consulted = Object.entries(summary.consulted)
    .filter(([, n]) => (n ?? 0) > 0)
    .map(([tier, n]) => `${TIER_LABELS[tier] ?? tier}: ${n} consulted`);
  const canContribute = summary.canContribute
    .map(t => TIER_LABELS[t] ?? t)
    .join(', ');
  if (consulted.length === 0 && !canContribute) return null;
  return (
    <p
      className="type-body text-text-muted leading-snug"
      data-testid="coverage-automatic-source-hint"
      title="Saved source evidence appears automatically. Document-only fields still require a source document."
    >
      {consulted.length > 0 && (
        <span>{consulted.join(' · ')}</span>
      )}
      {canContribute && (
        <span>
          {consulted.length > 0 ? ' · ' : ''}
          Available source channels: {canContribute}
        </span>
      )}
    </p>
  );
}

export const CoverageRequirementPanel: React.FC<CoverageRequirementPanelProps> = ({
  item: originalItem,
  authoringItem,
  status,
  authoredClaims = [],
  lineage,
  sed,
  coverage,
  matchCount = 0,
  topMatch = null,
  variant = 'responsive',
  onOpenAuthor,
  onOpenUploadFor,
  onOpenInDocuments,
  onOpenBaseline,
  onAddSupportingDoc,
  onReplaceDoc,
  onOpenLineage,
  onViewVariables,
  focusVariablePath = null,
  forceExpandVariables = false,
  siteContext,
  documents,
  siteId,
  onOpenDocument,
  onReloadSed,
  autoFillEntry,
  unifiedEvidencePanel,
  onAuthorSaved,
  reportTypes = [],
  projectIntent,
  typedPreferences,
  onOpenSiteIntent,
  decisionStage,
  onWaiveRequirement,
  onPreviousTopic,
  onNextTopic,
  sourceDetails,
}) => {
  const item = React.useMemo(() => originalItem ? evidenceRequirementForStage(originalItem, decisionStage, projectIntent) : null, [originalItem, decisionStage, projectIntent]);
  const unifiedPanel = unifiedEvidencePanel ?? isUnifiedEvidencePanelEnabled();
  const simpleFacts = decisionStage != null;
  const topClaim = authoredClaims[0];
  const isSetupRow = item != null && isNonDocumentRequirement(item);
  const setupReportType = item
    ? (setupReportTypeForRequirement(item.id) ?? reportTypes[0])
    : undefined;

  const [fieldFilter, setFieldFilter] = React.useState<'all' | 'required' | 'empty' | 'filled'>('all');
  React.useEffect(() => setFieldFilter('all'), [item?.id]);

  const automaticFill = React.useMemo(
    () => autoFillEntry ?? (item ? compileCoverageAutoFillFromSed(sed, {
      typedPreferences, projectIntent, siteContext: siteContext ?? undefined, reportTypes,
    })[item.id] : undefined),
    [autoFillEntry, item, sed, typedPreferences, projectIntent, siteContext, reportTypes],
  );

  const reportFields = React.useMemo<ReviewedEvidenceFields | null>(
    () => {
      if (!item) return null;
      if (isSetupRow) {
        const resolved = resolveReportSetupFields({
          requirement: item,
          reportType: setupReportType,
          projectIntent,
          siteContext,
          typedPreferences,
        });
        return evidenceFieldsWithRecordedUnknowns(resolved, item.id, projectIntent, decisionStage);
      }
      return evidenceResolvedFieldsForStage(resolveAutomaticReportFields({ requirement: item, sed, authoredClaim: topClaim, autoFillEntry: automaticFill }), item.id, decisionStage);
    },
    [item, isSetupRow, sed, topClaim, setupReportType, projectIntent, siteContext, typedPreferences, decisionStage, automaticFill],
  );

  const completeness = React.useMemo(() => {
    if (!item || !reportFields?.hasSchema) {
      return {
        structuredFilled: 0,
        structuredTotal: 0,
      };
    }
    return {
      structuredFilled: reportFields.statusFilled,
      structuredTotal: reportFields.statusTotal,
    };
  }, [item, reportFields]);

  const shellClass =
    variant === 'drawer'
      ? 'fixed inset-y-0 right-0 z-40 w-full max-w-[min(100vw,34rem)] border-l border-border bg-surface-1 shadow-xl lg:hidden'
      : variant === 'panel'
        ? 'hidden lg:flex lg:flex-col lg:min-h-[320px] lg:min-w-0 rounded-md border border-border-subtle bg-surface-1/80'
        : 'flex flex-col min-h-[320px] min-w-0 rounded-md border border-border-subtle bg-surface-1/80 overflow-hidden';

  const primaryFields = reportFields?.primary ?? [];
  const missingRequired = item
    ? primaryFields.filter(f => f.required && f.isEmpty && f.key !== 'notes')
    : [];
  const recordedUnknownKeys = reportFields?.recordedUnknownKeys ?? [];
  const reviewedFieldCount = reportFields?.reviewedFieldCount;
  const filledPrimary = item ? primaryFields.filter(f => !f.isEmpty || recordedUnknownKeys.includes(f.key)) : [];
  // TWO-STAGE — during early screening the Site Project Intent row asks only
  // for screening facts. Design and delivery fields are Stage 2 decisions,
  // recorded after screening: listed as later work, never as screening gaps
  // (so they drop out of the empty list and "closest wins").
  const emptyPrimaryAll = item ? primaryFields.filter(f => f.isEmpty && !recordedUnknownKeys.includes(f.key)) : [];
  const emptyPrimary = emptyPrimaryAll;
  const hasDeferredFields = decisionStage != null && item?.kind === 'site-intent';
  const relatedPopulated = item
    ? (reportFields?.related ?? []).filter(f => !f.isEmpty)
    : [];
  const relatedEmpty = item
    ? (reportFields?.related ?? []).filter(f => f.isEmpty)
    : [];
  const isWaterService = (path: string) => path === 'waterResource.municipalSupply' || path === 'waterResource.wastewaterService';
  const relatedVisible = (reportFields?.related ?? []).filter(f => !f.isEmpty || isWaterService(f.path));
  const relatedHiddenEmpty = relatedEmpty.filter(f => !isWaterService(f.path));

  const fieldPresentation = React.useMemo(
    () => {
      if (!item || emptyPrimary.length === 0) return null;
      
      const applicableReportTypes = reportTypes.filter(rt =>
        getDocumentChecklistFor(rt).some(req => req.id === item.id)
      );
      const presentationTypes = applicableReportTypes.length > 0 ? applicableReportTypes : reportTypes;

      return resolveRequirementFieldPresentation({
        requirement: item,
        reportTypes: presentationTypes,
        projectIntent,
        sed,
        emptyFieldKeys: emptyPrimary.map(f => f.key),
      });
    },
    [item, emptyPrimary, reportTypes, projectIntent, sed],
  );

  const orderedEmptyPrimary = React.useMemo(() => {
    if (!fieldPresentation) return emptyPrimary;
    const order = fieldPresentation.orderedFieldKeys;
    return [...emptyPrimary].sort(
      (a, b) => order.indexOf(a.key) - order.indexOf(b.key) || a.key.localeCompare(b.key),
    );
  }, [emptyPrimary, fieldPresentation]);

  const requiredEmpty = React.useMemo(() => orderedEmptyPrimary.filter(f => f.required), [orderedEmptyPrimary]);

  // Round4 — top intent-ranked empty fields while the row is below the
  // Strong bar. Empty when already Strong (or no schema) so the hint
  // strip disappears once there is nothing left to win.
  const closestWins = React.useMemo(() => {
    if (completeness.structuredTotal <= 0) return [] as string[];
    const strongThreshold = Math.ceil(completeness.structuredTotal * STRONG_STRUCTURED_FRACTION);
    if (Math.min(completeness.structuredFilled, completeness.structuredTotal) >= strongThreshold) {
      return [] as string[];
    }
    return orderedEmptyPrimary.slice(0, 3).map(f => f.label);
  }, [completeness, orderedEmptyPrimary]);

  if (!item || !lineage) {
    return (
      <aside
        className={`${shellClass} p-4 text-[11px] text-text-muted`}
        data-testid="coverage-requirement-panel-empty"
      >
        Select a requirement to review report fields, sources, and actions.
      </aside>
    );
  }

  return (
    <aside
      className={`${shellClass} flex flex-col overflow-hidden`}
      data-testid="coverage-requirement-panel"
      aria-label={`Requirement detail: ${item.label}`}
    >
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4">
        {/* Header */}
        <header className="space-y-2 border-b border-border-subtle pb-3">
          {simpleFacts && (onPreviousTopic || onNextTopic) && <nav className="flex justify-between gap-2" aria-label="Screening topic navigation">
            <button type="button" disabled={!onPreviousTopic} onClick={onPreviousTopic} className="text-[11px] text-text-muted hover:text-primary disabled:opacity-30 focus-visible:outline focus-visible:outline-primary">Previous topic</button>
            <button type="button" disabled={!onNextTopic} onClick={onNextTopic} className="text-[11px] text-text-muted hover:text-primary disabled:opacity-30 focus-visible:outline focus-visible:outline-primary">Next topic</button>
          </nav>}
          <p className="type-section text-primary" data-testid="coverage-panel-eyebrow">
            {decisionStage === 1 ? 'Screening facts' : decisionStage === 2 ? 'Validation facts' : 'Requirement worksheet'}
          </p>
          <h3 className="text-sm font-semibold text-text-primary">{item.label}</h3>
          {!simpleFacts && item.description && (
            <p className="text-[11px] text-text-muted leading-snug">{item.description}</p>
          )}
          {!simpleFacts && <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex h-6 w-6 items-center justify-center rounded-md border text-[11px] font-semibold ${CELL_CLASS[status]}`}
            >
              {CELL_GLYPH[status]}
            </span>
            <span className="text-[11px] font-medium text-text-primary">{CELL_LABEL[status]}</span>
            {matchCount > 0 && status !== 'authored' && status !== 'missing' && (
              <span className="text-[10px] text-text-muted">· {matchCount} doc{matchCount === 1 ? '' : 's'}</span>
            )}
          </div>}
          {!simpleFacts && completeness.structuredTotal > 0 && (
            <CompletenessBar
              filled={completeness.structuredFilled}
              total={completeness.structuredTotal}
              reviewedContext={decisionStage != null && item.kind === 'site-intent'}
              reviewed={reviewedFieldCount}
            />
          )}
        </header>

        {evidenceProbeFor(item) && (() => {
          // P3.8 follow-through — an evidence-probed row has no fields to
          // author; the action is the scan itself, and the verdict's reason
          // says why it is missing.
          const verdict = evidenceProbeFor(item)!(sed);
          return (
            <div className="mt-2 flex flex-col gap-1" data-testid="coverage-evidence-probe">
              {verdict.reason && (
                <p className="type-body text-text-muted" data-testid="coverage-evidence-probe-reason">{verdict.reason}</p>
              )}
              {siteId && verdict.status === 'missing' && (
                <PsePrefetchCta
                  siteId={siteId}
                  missingSections={item.sedSectionsHelpful}
                  onSuccess={() => onReloadSed?.()}
                />
              )}
            </div>
          );
        })()}


        {/* Round4 — "closest wins": the intent-ranked next empty fields
            (resolveRequirementFieldPresentation order), so the user knows
            WHICH fields move this row toward Strong fastest. */}
        {!simpleFacts && closestWins.length > 0 && (
          <p
            className="rounded-sm border border-warning/30 bg-warning/5 px-2 py-1.5 type-caption text-warning"
            data-testid="coverage-panel-closest-wins"
          >
            Closest wins: {closestWins.join(', ')}.
          </p>
        )}

        {/* Report fields */}
        {simpleFacts && reportFields ? (
          <ScreeningFacts key={item.id} item={item} authoringItem={authoringItem ?? originalItem ?? item} fields={reportFields} sed={sed} siteId={siteId}
            documents={documents} existingClaim={topClaim} onOpenAuthor={onOpenAuthor}
            onOpenSiteIntent={onOpenSiteIntent} onViewVariables={onViewVariables}
            onSaved={onAuthorSaved} onReloadSed={onReloadSed} />
        ) : (
        <section className="space-y-2" data-testid="coverage-requirement-report-fields">
          {reportFields?.hasSchema && primaryFields.length > 0 && (
            <div className="flex items-center gap-1.5 mb-2" role="group" aria-label="Field filter">
              {([ 
                { id: 'all',      label: `All ${primaryFields.length + (reportFields?.related.length ?? 0)}` },
                { id: 'required', label: `Required ${requiredEmpty.length}` },
                { id: 'empty',    label: `${simpleFacts ? 'Unknown' : 'Empty'} ${orderedEmptyPrimary.length + relatedEmpty.length}` },
                { id: 'filled',   label: `${simpleFacts ? 'Known' : decisionStage != null && item.kind === 'site-intent' ? 'Reviewed' : 'Filled'} ${filledPrimary.length + relatedPopulated.length}` },
              ] as const).filter(f => !simpleFacts || f.id !== 'required').map(f => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFieldFilter(f.id)}
                  className={`rounded-full border px-2 py-0.5 type-caption transition-colors
                    ${fieldFilter === f.id
                      ? 'bg-primary/15 border-primary/40 text-primary'
                      : 'border-border text-text-muted hover:text-text-primary hover:border-border-subtle'}
                  `}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-1">
            <h4 className="type-section text-text-secondary">
              {simpleFacts ? 'Known and unknown facts' : 'Report fields'}
            </h4>
            <span title={SOURCE_TOOLTIP} className="text-text-muted" data-testid="coverage-report-fields-source-help">
              <HelpCircle className="w-3 h-3" aria-label="Field source legend" />
            </span>
          </div>

          {hasDeferredFields && (
            <p
              className="rounded-sm border border-border-subtle px-2 py-1.5 type-caption text-text-muted"
              data-testid="coverage-panel-stage2-deferred"
            >
              {simpleFacts
                ? 'Only screening context is reviewed here. Engineering choices belong to Stage 2; commercial and annual-accounting inputs remain in their specialist editors.'
                : 'Only screening context and engineering inputs are reviewed here. Commercial and annual-accounting inputs remain in their specialist editors.'}
              {' '}Saved values and source provenance are retained.
            </p>
          )}

          {/* Post-closure review surface: extracted values stay reviewable
              here even after the gap card left the Evidence worklist. */}
          {item && !isSetupRow && (
            <RequirementFieldReviewHost
              item={item}
              sed={sed}
              siteId={siteId}
              documents={documents}
              onReloadSed={onReloadSed}
            />
          )}

          {fieldFilter === 'all' ? (
            <>
          {!reportFields?.hasSchema && (
            <p className="text-[10px] text-text-muted italic" data-testid="coverage-report-fields-prose-only">
              Prose-only requirement — use Author to add notes.
            </p>
          )}

          {missingRequired.length > 0 && (
            <ul className="space-y-0.5 rounded-sm border border-warning/30 bg-warning/5 p-2 text-[10px]" data-testid="coverage-report-fields-missing-required">
              {missingRequired.map(f => (
                <li key={f.key} className="text-warning">
                  Required: {f.label}
                </li>
              ))}
            </ul>
          )}

          {filledPrimary.length > 0 && (
            <ul className="space-y-2">
              {filledPrimary.map(f => (
                <li
                  key={f.key}
                  className={`${FIELD_ROW_CLASS} space-y-0.5`}
                  data-testid={`coverage-report-field-${f.key}`}
                >
                  <span className="text-text-muted">
                    {f.label}
                    {f.required ? ' *' : ''}
                  </span>
                  <span
                    className="font-mono text-text-primary break-words min-w-0"
                    title={f.displayValue}
                  >
                    {f.displayValue}
                  </span>
                  <div className="flex flex-wrap items-center gap-1 md:justify-end md:justify-self-end">
                    {f.provenance !== 'missing' && (
                      <FieldProvenanceChip
                        provenance={f.provenance}
                        sourceLabel={f.sourceLabel}
                      />
                    )}
                    <button
                      type="button"
                      onClick={onOpenAuthor}
                      className="type-body text-text-muted hover:text-primary"
                      data-testid={`coverage-report-field-edit-${f.key}`}
                      title={`Edit ${f.label} via Author`}
                    >
                      <Pencil className="w-2.5 h-2.5" aria-label={`Edit ${f.label}`} />
                    </button>
                  </div>
                  {f.provenance === 'authored' && f.authoredBy && (
                    <p className="md:col-span-3 type-body text-text-muted">
                      By {f.authoredBy}
                      {f.authoredAt ? ` · ${f.authoredAt}` : ''}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {emptyPrimary.length > 0 && (
            siteId && reportFields?.hasSchema && item ? (
              item.kind === 'report-setup' && setupReportType ? (
                <RequirementInlineSetupForm
                  siteId={siteId}
                  item={item}
                  reportType={setupReportType}
                  typedPreferences={typedPreferences}
                  projectIntent={projectIntent}
                  fieldKeys={fieldPresentation?.orderedFieldKeys ?? emptyPrimary.map(f => f.key)}
                  onSaved={() => onReloadSed?.()}
                />
              ) : item.kind === 'site-intent' ? (
                onOpenSiteIntent ? (
                  <button
                    type="button"
                    onClick={() => onOpenSiteIntent?.()}
                    className="text-[10px] font-semibold text-primary hover:underline"
                    data-testid="coverage-edit-site-intent"
                  >
                    Edit Site Project Intent →
                  </button>
                ) : null
              ) : (
                simpleFacts ? (
                  <>
                    <ul className="space-y-1.5">
                      {orderedEmptyPrimary.map(field => (
                        <li key={field.key} className={FIELD_ROW_CLASS} data-testid={`coverage-report-field-${field.key}`}>
                          <span className="text-text-muted">{field.label}</span>
                          <span className="text-text-muted">Unknown</span>
                        </li>
                      ))}
                    </ul>
                    <details key={item.id} className="rounded-md border border-border-subtle p-2" data-testid="coverage-screening-add-facts">
                      <summary className="cursor-pointer type-caption text-primary">Add known facts</summary>
                      <div className="mt-2">
                <RequirementInlineAuthorForm
                  siteId={siteId}
                  item={item}
                  existingClaim={topClaim}
                  fieldKeys={fieldPresentation?.orderedFieldKeys ?? emptyPrimary.map(f => f.key)}
                  sectionIntro={fieldPresentation?.intro}
                  fieldHints={fieldPresentation?.hintsByKey}
                  onSaved={(result) => {
                    onAuthorSaved?.(result);
                    onReloadSed?.();
                  }}
                />
                      </div>
                    </details>
                  </>
                ) : (
                <RequirementInlineAuthorForm
                  siteId={siteId}
                  item={item}
                  existingClaim={topClaim}
                  fieldKeys={fieldPresentation?.orderedFieldKeys ?? emptyPrimary.map(f => f.key)}
                  sectionIntro={fieldPresentation?.intro}
                  fieldHints={fieldPresentation?.hintsByKey}
                  onSaved={(result) => {
                    onAuthorSaved?.(result);
                    onReloadSed?.();
                  }}
                />
                )
              )
            ) : (
              <ul className="space-y-1.5">
                {orderedEmptyPrimary.map(f => {
                  const hint = fieldPresentation?.hintsByKey[f.key];
                  return (
                    <li
                      key={f.key}
                      className={`${FIELD_ROW_CLASS} text-text-muted`}
                      data-testid={`coverage-report-field-${f.key}`}
                    >
                      <span>
                        {f.label}
                        {f.required ? ' *' : ''}
                        {hint?.recommended ? ' · Recommended' : ''}
                      </span>
                      <span className="font-mono md:col-span-2 italic text-[9px] leading-snug">
                        {hint?.guidance ?? '—'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )
          )}

          {(reportFields?.related.length ?? 0) > 0 && (
            <section
              className="text-[10px] space-y-2"
              data-testid="coverage-report-fields-related"
            >
              <h5 className="type-section text-text-secondary">
                Related site data ({relatedPopulated.length}/{reportFields!.related.length} known)
              </h5>
              {item?.id === 'municipal-utility-letter' && (
                <p className="type-caption text-text-muted">
                  Regional water stress does not confirm site supply, allocation or wastewater service.
                </p>
              )}
              <ul className="space-y-1.5">
                {relatedVisible.map(f => (
                  <li
                    key={f.path}
                    className={FIELD_ROW_CLASS}
                    data-testid={`coverage-panel-field-${f.path}`}
                  >
                    <span className="text-text-muted">{f.label}</span>
                    <span
                      className="font-mono text-text-primary break-words min-w-0"
                      title={f.displayValue}
                    >
                      {f.displayValue}
                    </span>
                    <div className="flex flex-wrap items-center gap-1 md:justify-end md:justify-self-end">
                      {!f.isEmpty && <FieldProvenanceChip provenance={f.provenance} sourceLabel={f.sourceLabel} />}
                      {onViewVariables && (
                        <button
                          type="button"
                          onClick={() => onViewVariables(f.path)}
                          className="type-body text-primary hover:underline whitespace-nowrap"
                          data-testid={`coverage-report-field-vars-${f.path}`}
                        >
                          {unifiedPanel ? 'Show variable' : 'Variables'}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
                {relatedHiddenEmpty.length > 0 && (
                  <li className="text-text-muted italic md:col-span-3">
                    {relatedHiddenEmpty.length} additional registry field{relatedHiddenEmpty.length === 1 ? '' : 's'} not yet populated
                  </li>
                )}
              </ul>
            </section>
          )}
            </>
          ) : (
            <ul className="space-y-2">
              {fieldFilter === 'required' && requiredEmpty.map(f => {
                const hint = fieldPresentation?.hintsByKey[f.key];
                return (
                  <li key={f.key} className={`${FIELD_ROW_CLASS} text-text-muted`} data-testid={`coverage-report-field-${f.key}`}>
                    <span>{f.label}{f.required ? ' *' : ''}{hint?.recommended ? ' · Recommended' : ''}</span>
                    <span className="font-mono md:col-span-2 italic text-[9px] leading-snug">{hint?.guidance ?? '—'}</span>
                  </li>
                );
              })}
              {fieldFilter === 'empty' && [
                ...orderedEmptyPrimary.map(f => {
                  const hint = fieldPresentation?.hintsByKey[f.key];
                  return (
                    <li key={f.key} className={`${FIELD_ROW_CLASS} text-text-muted`} data-testid={`coverage-report-field-${f.key}`}>
                      <span>{f.label}{f.required ? ' *' : ''}{hint?.recommended ? ' · Recommended' : ''}</span>
                      <span className="font-mono md:col-span-2 italic text-[9px] leading-snug">{hint?.guidance ?? '—'}</span>
                    </li>
                  );
                }),
                ...relatedEmpty.map(f => (
                  <li key={f.path} className={`${FIELD_ROW_CLASS} text-text-muted`} data-testid={`coverage-panel-field-${f.path}`}>
                    <span>{f.label}</span>
                    <span className="font-mono md:col-span-2 italic text-[9px] leading-snug">—</span>
                  </li>
                ))
              ]}
              {fieldFilter === 'filled' && [
                ...filledPrimary.map(f => (
                  <li key={f.key} className={`${FIELD_ROW_CLASS} space-y-0.5`} data-testid={`coverage-report-field-${f.key}`}>
                    <span className="text-text-muted">{f.label}{f.required ? ' *' : ''}</span>
                    <span className="font-mono text-text-primary break-words min-w-0" title={f.displayValue}>{f.displayValue}</span>
                    <div className="flex flex-wrap items-center gap-1 md:justify-end md:justify-self-end">
                      {f.provenance !== 'missing' && <FieldProvenanceChip provenance={f.provenance} sourceLabel={f.sourceLabel} />}
                      <button type="button" onClick={onOpenAuthor} className="type-body text-text-muted hover:text-primary" data-testid={`coverage-report-field-edit-${f.key}`} title={`Edit ${f.label} via Author`}>
                        <Pencil className="w-2.5 h-2.5" aria-label={`Edit ${f.label}`} />
                      </button>
                    </div>
                    {f.provenance === 'authored' && f.authoredBy && (
                      <p className="md:col-span-3 type-body text-text-muted">By {f.authoredBy}{f.authoredAt ? ` · ${f.authoredAt}` : ''}</p>
                    )}
                  </li>
                )),
                ...relatedPopulated.map(f => (
                  <li key={f.path} className={FIELD_ROW_CLASS} data-testid={`coverage-panel-field-${f.path}`}>
                    <span className="text-text-muted">{f.label}</span>
                    <span className="font-mono text-text-primary break-words min-w-0" title={f.displayValue}>{f.displayValue}</span>
                    <div className="flex flex-wrap items-center gap-1 md:justify-end md:justify-self-end">
                      <FieldProvenanceChip provenance={f.provenance} sourceLabel={f.sourceLabel} />
                      {onViewVariables && (
                        <button type="button" onClick={() => onViewVariables(f.path)} className="type-body text-primary hover:underline whitespace-nowrap" data-testid={`coverage-report-field-vars-${f.path}`}>
                          {unifiedPanel ? 'Show variable' : 'Variables'}
                        </button>
                      )}
                    </div>
                  </li>
                ))
              ]}
            </ul>
          )}
        </section>
        )}

        {/* In-panel extracted variables (Phase 1 unified panel) */}
        {sed && !simpleFacts && (
          <RequirementVariableAccordion
            requirementId={item.id}
            sed={sed}
            siteContext={siteContext}
            documents={documents ? [...documents] : undefined}
            siteId={siteId}
            focusVariablePath={focusVariablePath}
            forceExpanded={forceExpandVariables}
            onOpenDocument={onOpenDocument}
            onReloadSed={onReloadSed}
          />
        )}

        {/* Sources */}
        <details open={!simpleFacts || Boolean(focusVariablePath) || forceExpandVariables} className="space-y-2" data-testid="coverage-requirement-sources">
          <summary className="cursor-pointer text-[11px] text-text-muted">{simpleFacts ? 'Source details' : 'Sources'}</summary>
        <section className="space-y-2">
          {sourceDetails}
          {simpleFacts && sed && (focusVariablePath || forceExpandVariables) && <RequirementVariableAccordion
            key={item.id} requirementId={item.id} sed={sed} siteContext={siteContext}
            documents={documents ? [...documents] : undefined} siteId={siteId}
            focusVariablePath={focusVariablePath} forceExpanded={forceExpandVariables}
            onOpenDocument={onOpenDocument} onReloadSed={onReloadSed} />}
          <ul className="space-y-1">
            {SOURCE_SUMMARY_ROWS.map(row => {
              const n = sourceCount(lineage, row.kind);
              if (n <= 0 && row.kind !== 'research') return null;
              if (row.kind === 'research' && n <= 0) return null;
              return (
                <li
                  key={row.kind}
                  className="flex items-center gap-2 text-[11px] text-text-primary"
                  data-testid={`coverage-source-row-${row.kind}`}
                >
                  <span aria-hidden="true">{row.icon}</span>
                  <span>
                    {row.kind === 'research' ? row.label : `${n} ${n === 1 ? row.label : row.plural}`}
                  </span>
                </li>
              );
            })}
            {lineage.sources.liveLiterature && (
              <li
                className="flex items-start gap-2 text-[11px] text-info"
                data-testid="coverage-source-row-live-literature"
                title={
                  lineage.sources.liveLiterature.topEntry
                    ? `${lineage.sources.liveLiterature.entryCount} paper${
                        lineage.sources.liveLiterature.entryCount === 1 ? '' : 's'
                      } — top: ${lineage.sources.liveLiterature.topEntry.title}`
                    : `${lineage.sources.liveLiterature.entryCount} paper${
                        lineage.sources.liveLiterature.entryCount === 1 ? '' : 's'
                      }`
                }
              >
                <span aria-hidden="true">📚</span>
                <span>
                  {lineage.sources.liveLiterature.entryCount} live literature paper
                  {lineage.sources.liveLiterature.entryCount === 1 ? '' : 's'} — report prompt only
                  (does not fill fields)
                </span>
              </li>
            )}
          </ul>
          {coverage && coverage.fillSource !== 'missing' && (
            <CoverageCellFillSource coverage={coverage} reqId={item.id} />
          )}
          {coverage?.rationale && coverage.fillSource !== 'missing' && (
            <p className="text-[10px] text-text-muted italic" data-testid="coverage-requirement-caf-rationale">
              {coverage.rationale}
            </p>
          )}
          {onOpenLineage && (
            <button
              type="button"
              onClick={() => onOpenLineage(lineage)}
              className="text-[10px] font-semibold text-primary hover:underline"
              data-testid="coverage-requirement-view-lineage"
            >
              View evidence chain
            </button>
          )}
        </section>
        </details>
      </div>

      {/* Actions footer */}
      <footer
        className="shrink-0 border-t border-border-subtle p-3 flex flex-wrap gap-2"
        data-testid="coverage-requirement-actions"
      >
        {!simpleFacts && !isSetupRow && (
          <>
        <button
          type="button"
          onClick={onOpenAuthor}
          className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 text-[10px] font-semibold hover:bg-state-hover"
          data-testid={`coverage-matrix-author-${item.id}`}
        >
          <Pencil className="w-3 h-3" />
          {simpleFacts ? 'Add or update facts' : authoredClaims.length > 0 ? 'Edit author' : 'Author'}
        </button>
        {!simpleFacts && onWaiveRequirement && status !== 'waived' && status !== 'strong' && (
          <button
            type="button"
            onClick={() => {
              const reason = window.prompt('Why is this requirement being waived?');
              if (reason) {
                // Defaulting to 'accepted-gap' for now, can be expanded later
                onWaiveRequirement(item.id, reason, 'accepted-gap').catch(console.error);
              }
            }}
            className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 text-[10px] font-semibold hover:bg-state-hover"
            data-testid={`coverage-matrix-waive-${item.id}`}
          >
            Waive requirement
          </button>
        )}
        {simpleFacts && !isSetupRow && <>
          <button type="button" onClick={onOpenUploadFor} className="inline-flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-2.5 py-1.5 text-[11px] text-primary hover:bg-primary/15 focus-visible:outline focus-visible:outline-primary" data-testid="coverage-panel-upload"><Upload className="h-3 w-3" aria-hidden />Upload document</button>
          <button type="button" onClick={onOpenInDocuments} className="rounded-sm px-2.5 py-1.5 text-[11px] text-text-muted hover:text-primary focus-visible:outline focus-visible:outline-primary" data-testid="coverage-panel-view-docs">View documents</button>
        </>}
        {reportFields?.hasSchema && (
          <div className="flex flex-col gap-1 w-full">
            <p className="type-body text-text-muted" data-testid="coverage-panel-automatic-fields">
              Fields update automatically from saved site and document evidence. Missing source facts remain unknown.
            </p>
            {AUTOMATIC_SOURCE_HINTS[item.id] && (
              <p
                className="type-body text-text-muted leading-snug"
                data-testid="coverage-panel-automatic-hint"
              >
                {AUTOMATIC_SOURCE_HINTS[item.id]}
              </p>
            )}
            {lineage && <AutomaticSourceHint lineage={lineage} />}
          </div>
        )}
        {status === 'missing' ? (
          <>
            <button
              type="button"
              onClick={onOpenUploadFor}
              className="inline-flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[10px] font-semibold text-primary hover:bg-primary/15"
              data-testid="coverage-panel-upload"
            >
              <Upload className="w-3 h-3" />
              Upload
            </button>
            {onOpenBaseline && (
              <button
                type="button"
                onClick={onOpenBaseline}
                className="text-[10px] text-text-secondary hover:underline"
                data-testid="coverage-panel-baseline"
              >
                Use baseline
              </button>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onOpenInDocuments}
              className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 text-[10px] hover:bg-state-hover"
              data-testid="coverage-panel-view-docs"
            >
              <ExternalLink className="w-3 h-3" />
              View docs
            </button>
            {onReplaceDoc && topMatch && (
              <button
                type="button"
                onClick={onReplaceDoc}
                title={`Replace ${topMatch.doc.fileName}`}
                className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 text-[10px] hover:bg-state-hover"
                data-testid={`coverage-matrix-replace-${item.id}`}
              >
                Replace
              </button>
            )}
            <button
              type="button"
              onClick={() => (onAddSupportingDoc ? onAddSupportingDoc() : onOpenUploadFor())}
              className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 text-[10px] hover:bg-state-hover"
              data-testid={`coverage-matrix-add-supporting-${item.id}`}
            >
              <Plus className="w-3 h-3" />
              Add supporting
            </button>
          </>
        )}
          </>
        )}
        {item.kind === 'site-intent' && onOpenSiteIntent && (
          <button
            type="button"
            onClick={() => onOpenSiteIntent?.()}
            className="inline-flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[10px] font-semibold text-primary hover:bg-primary/15"
            data-testid="coverage-panel-edit-site-intent"
          >
            Edit Site Project Intent
          </button>
        )}
        {onViewVariables && (!unifiedPanel || simpleFacts) && (
          <button
            type="button"
            onClick={() => onViewVariables()}
            className="text-[10px] text-primary hover:underline ml-auto"
            data-testid="coverage-panel-view-variables"
          >
            {simpleFacts ? 'Inspect source data' : 'View in Extracted Variables'}
          </button>
        )}
        {onViewVariables && unifiedPanel && !simpleFacts && (
          <button
            type="button"
            onClick={() => onViewVariables()}
            className="inline-flex items-center gap-0.5 text-[10px] text-primary hover:underline ml-auto"
            data-testid="coverage-panel-expand-variables"
          >
            <ChevronRight className="w-3 h-3" aria-hidden />
            Expand all variables
          </button>
        )}
      </footer>
    </aside>
  );
};
